import type { LayoutElement, LayoutProject, FloorPlanBounds } from '../types';
import type { Layout3DProject } from './layout3dGenerate';
import { apply3DDefaultsOnImport } from './layout3d';

const DEFAULT_LAYER_ID = 'layer-default';

export function isLayout3DProject(data: unknown): data is Layout3DProject {
  if (!data || typeof data !== 'object') return false;
  const d = data as Record<string, unknown>;
  return Boolean(d.elements && d.bounds && d.boundsKey && d.objectCount != null);
}

export function isLayoutProject(data: unknown): data is LayoutProject {
  if (!data || typeof data !== 'object') return false;
  const d = data as Record<string, unknown>;
  return Boolean(d.version && d.elements);
}

/** True unless explicitly hidden. */
export function isElementVisible(element: LayoutElement): boolean {
  return element.metadata?.visible !== false;
}

export function elementLayerId(element: LayoutElement): string {
  return element.metadata?.layerId || element.layerId || DEFAULT_LAYER_ID;
}

export function isLayerHidden(element: LayoutElement, hiddenLayerIds: Set<string>): boolean {
  const lid = elementLayerId(element);
  return hiddenLayerIds.has(lid);
}

/** True when imported JSON has explicit elevation (position.z or metadata.zIndex). */
export function layoutHasExplicitElevation(elements: Record<string, LayoutElement>): boolean {
  for (const el of Object.values(elements)) {
    if (el.position?.z !== undefined && el.position?.z !== null) return true;
    if (el.metadata?.zIndex !== undefined && el.metadata?.zIndex !== null) return true;
  }
  return false;
}

/** Runtime fallback: flat plan when no element carries metadata.zIndex. */
export function layoutUsesFlatPlanView(
  elements: Record<string, LayoutElement>,
  order: string[]
): boolean {
  if (order.length === 0) return false;
  for (const id of order) {
    const el = elements[id];
    if (!el) continue;
    if (el.metadata?.zIndex !== undefined && el.metadata?.zIndex !== null) return false;
  }
  return true;
}

/** Axis-aligned bounds of a layout element, accounting for rotation about its center. */
export function elementAxisAlignedBounds(el: LayoutElement): FloorPlanBounds {
  const x = el.position.x;
  const y = el.position.y;
  const w = Math.max(0, el.dimensions?.width ?? 0);
  const h = Math.max(0, el.dimensions?.height ?? 0);
  const rot = el.rotation || 0;
  if (!rot || w === 0 || h === 0) {
    return {
      minX: Math.min(x, x + w),
      maxX: Math.max(x, x + w),
      minY: Math.min(y, y + h),
      maxY: Math.max(y, y + h),
    };
  }
  const rad = (rot * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const cx = x + w / 2;
  const cy = y + h / 2;
  const corners = [
    { lx: -w / 2, ly: -h / 2 },
    { lx: w / 2, ly: -h / 2 },
    { lx: w / 2, ly: h / 2 },
    { lx: -w / 2, ly: h / 2 },
  ];
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const c of corners) {
    const wx = cx + c.lx * cos - c.ly * sin;
    const wy = cy + c.lx * sin + c.ly * cos;
    minX = Math.min(minX, wx);
    maxX = Math.max(maxX, wx);
    minY = Math.min(minY, wy);
    maxY = Math.max(maxY, wy);
  }
  return { minX, minY, maxX, maxY };
}

/** Bounding box for an imported layout (no DXF floor required). */
export function computeLayoutBoundsFromElements(
  elements: Record<string, LayoutElement>,
  order: string[]
): FloorPlanBounds | null {
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  for (const id of order) {
    const el = elements[id];
    if (!el || !isElementVisible(el)) continue;
    const b = elementAxisAlignedBounds(el);
    minX = Math.min(minX, b.minX);
    maxX = Math.max(maxX, b.maxX);
    minY = Math.min(minY, b.minY);
    maxY = Math.max(maxY, b.maxY);
  }

  if (!Number.isFinite(minX)) return null;
  return { minX, minY, maxX, maxY };
}

/**
 * Bounds of equipment overlay footprints. An equipment-only layout has no
 * elements to frame, so the viewport has to fall back to the overlays or it
 * stays wherever it was and they land off-screen.
 */
export function computeEquipmentOverlayBounds(
  instances: Iterable<{ polygon?: Array<{ x: number; y: number }> }>
): FloorPlanBounds | null {
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  for (const inst of instances) {
    if (!inst?.polygon) continue;
    for (const point of inst.polygon) {
      minX = Math.min(minX, point.x);
      maxX = Math.max(maxX, point.x);
      minY = Math.min(minY, point.y);
      maxY = Math.max(maxY, point.y);
    }
  }

  if (!Number.isFinite(minX)) return null;
  return { minX, minY, maxX, maxY };
}

export function importBoundsKey(bounds: FloorPlanBounds): string {
  return `${Math.round(bounds.minX)}_${Math.round(bounds.minY)}_${Math.round(bounds.maxX)}_${Math.round(bounds.maxY)}`;
}

/** Prepare elements from imported JSON for 2D canvas and 3D rendering. */
export function normalizeImportedElements(
  elements: Record<string, LayoutElement>,
  order: string[],
  mmPerCanvasUnit = 20,
  options?: { flatPlan?: boolean }
): { elements: Record<string, LayoutElement>; order: string[]; flatPlan: boolean } {
  const flatPlan = options?.flatPlan ?? !layoutHasExplicitElevation(elements);
  const out: Record<string, LayoutElement> = {};

  for (const id of order) {
    const raw = elements[id];
    if (!raw) continue;

    const with3d = apply3DDefaultsOnImport(raw, mmPerCanvasUnit);
    const layerId = raw.metadata?.layerId || raw.layerId || DEFAULT_LAYER_ID;
    out[id] = {
      ...with3d,
      layerId,
      metadata: {
        name: with3d.metadata?.name ?? with3d.type,
        locked: with3d.metadata?.locked ?? false,
        visible: with3d.metadata?.visible !== false,
        color: with3d.metadata?.color,
        opacity: with3d.metadata?.opacity,
        zIndex: with3d.metadata?.zIndex,
        layerId,
        groupId: with3d.metadata?.groupId,
      },
    };
  }

  const normalizedOrder = order.filter((id) => Boolean(out[id]));
  if (normalizedOrder.length === 0) {
    normalizedOrder.push(...Object.keys(out));
  }

  return { elements: out, order: normalizedOrder, flatPlan };
}

export function orderFromProject(project: LayoutProject | Layout3DProject): string[] {
  const elements = project.elements ?? {};
  const ids = Object.keys(elements);
  if (!Array.isArray(project.elementOrder) || project.elementOrder.length === 0) {
    return ids;
  }
  // Keep declared order, then append any elements the backend left out of elementOrder
  // (common for saved equipment flattened into `elements`).
  const ordered = project.elementOrder.filter((id) => Boolean(elements[id]));
  const seen = new Set(ordered);
  for (const id of ids) {
    if (!seen.has(id)) ordered.push(id);
  }
  return ordered;
}
